/**
 * Prompt library enums — the small fixed sets that live in code.
 *
 * Prompt CONTENT (bodies, variable options, shared lists) is data; these are the
 * vocabularies the application itself branches on.
 */

/**
 * WHO consumes a prompt — not what task it performs (ADR-0034).
 *
 * The audience is what the code can act on: it decides whether a variable is a form
 * field a person fills or a value the router supplies, which is why an `each` array
 * belongs only to a system prompt. A task-shaped set could not answer that, and two
 * of its five members differed in nothing but a default temperature.
 */
export enum PromptType {
	/** A person picks it and fills its variables. */
	USER_USE_CASE = 'user_use_case',
	/** The router injects it; its variables are supplied by code. */
	SYSTEM_USE_CASE = 'system_use_case',
	/**
	 * A governance check. Distinct from the two above because `temperature: 0` is a
	 * correctness requirement here, not a preference — a check that answers
	 * differently on identical input is not a check.
	 */
	VALIDATION = 'validation',
}

/**
 * What shape a prompt asks the model to produce.
 *
 * DERIVED from `outputSchema` rather than stored beside it: absent means text,
 * present means JSON. A separate column could contradict the schema — mode `json`
 * with no schema is a state every reader would need its own rule for — so the
 * schema's presence IS the mode.
 */
export enum PromptOutputMode {
	TEXT = 'text',
	JSON = 'json',
}

/** A prompt head's lifecycle. `archived` prompts drop out of tuple resolution. */
export enum PromptLifecycle {
	DRAFT = 'draft',
	PUBLISHED = 'published',
	ARCHIVED = 'archived',
}

/**
 * The declared type of a prompt variable.
 *
 * `ENUM` options are owned by the prompt itself; `LIST` options come from a
 * shared `prompt_variable_list` so they are maintained once across every referencing
 * prompt.
 */
export enum PromptVariableType {
	STRING = 'string',
	TEXT = 'text',
	NUMBER = 'number',
	BOOLEAN = 'boolean',
	DATE = 'date',
	ENUM = 'enum',
	LIST = 'list',
	/**
	 * Other variables joined into one phrase, contributing nothing when they are all
	 * empty.
	 *
	 * Takes no value of its own — it composes the values already collected. The
	 * problem it solves is that a template cannot express "these, separated, but only
	 * the ones that were filled": writing `{{a}}, {{b}}, {{c}}` renders `", , "` when
	 * a and c are unset, and a prompt full of orphaned punctuation is a prompt the
	 * model has to guess at.
	 *
	 * Declared once as `joins` so any prompt assembling optional fragments gets it —
	 * a scene from its qualifiers, an address from its lines, a persona from whatever
	 * traits a caller happened to supply.
	 */
	JOIN = 'join',
	/**
	 * An array of OBJECTS, rendered by repeating a per-item template.
	 *
	 * Distinct from `list` (which picks scalars from a shared option set) because
	 * `String(obj)` yields `[object Object]` — a structured item such as a governance
	 * rule needs its fields laid out, not stringified.
	 */
	EACH = 'each',
}

/**
 * Environment pointers. Callers resolve by label rather than computing a maximum
 * version, so promotion and rollback are repoints rather than republishes.
 */
export enum PromptLabel {
	LATEST = 'latest',
	PRODUCTION = 'production',
}

/** Whether a test case was authored by a human or drafted by GenAI. */
export enum PromptTestCaseOrigin {
	MANUAL = 'manual',
	GENERATED = 'generated',
}

/** The assertion vocabulary an eval case can use. */
export enum PromptAssertionType {
	CONTAINS = 'contains',
	NOT_CONTAINS = 'not_contains',
	MATCHES_OUTPUT_SCHEMA = 'matches_output_schema',
	MAX_LENGTH = 'max_length',
	/** A natural-language criterion judged by a model. */
	LLM_RUBRIC = 'llm_rubric',
}

/**
 * Eval-run status.
 *
 * `COMPLETED` means the RUN finished, not that its assertions passed — pass/fail
 * lives in the per-case outcomes and the summary counts. Conflating the two would
 * make "the prompt is bad" indistinguishable from "ai-router was down".
 */
export enum EvalRunStatus {
	PENDING = 'pending',
	RUNNING = 'running',
	COMPLETED = 'completed',
	/** Dispatch or run-level failure — not an assertion failure. */
	FAILED = 'failed',
	/** Superseded by a newer publish, or abandoned. */
	CANCELLED = 'cancelled',
}

/**
 * Per-case outcome, deliberately a separate axis from `EvalRunStatus` so an
 * infrastructure error is never counted as a failed assertion.
 */
export enum EvalCaseOutcome {
	PASSED = 'passed',
	FAILED = 'failed',
	/** Execution never produced a judgeable output (provider error, timeout). */
	ERRORED = 'errored',
}

/**
 * Prompts that our own code resolves by name.
 *
 * Registering a code here makes a typo a compile error, keeps renames to one
 * place, and lets grep answer "who uses this prompt". Tenant-authored prompts
 * that no code path names do NOT need an entry — this is not a registry of every
 * prompt, only of the ones code depends on.
 *
 * A startup/integration check verifies every member exists for the tenant, so
 * drift is caught before a render fails in production.
 */
export enum PromptCode {
	// Seeded as the library's first code-referenced prompts; extend as agents
	// migrate off inline strings.
	MESSAGE_TRANSLATION = 'message-translation',
}

const CODE_REFERENCED_PROMPTS: ReadonlySet<string> = new Set(Object.values(PromptCode))

/**
 * Whether live code resolves this prompt by name, and so must not lose it.
 *
 * Derived from {@link PromptCode} rather than stored on the row: a column would
 * be a second source of truth for the same fact, and the two would drift the
 * first time someone added a code without remembering to set the flag. Shared
 * between the delete guard and the UI that hides the control, so both agree on
 * what "protected" means.
 */
export const isCodeReferencedPrompt = (code: string): boolean => CODE_REFERENCED_PROMPTS.has(code)

/**
 * What a stored generation context is attached to.
 *
 * Deliberately NOT `EventEntityType`: that enum is the audit surface's, and its
 * members grow whenever something becomes auditable. The set of things a prompt
 * can render FOR is a narrower question owned here, so the two move independently
 * — an entity becoming auditable should not silently widen what a context row may
 * point at.
 *
 * Adding a member is how a new domain adopts generation context; no migration is
 * needed, because the column stores the value as text.
 */
export enum PromptContextEntityType {
	WORK_ORDER = 'work_order',
	ARTICLE = 'article',
}
